import { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { HiArrowRight, HiArrowLeft } from 'react-icons/hi2';
import MarketplaceProductCard from '@c/marketplace/MarketplaceProductCard';
import { ProductDocument } from '@util/types/firestore/products';
import { ListingDisplay } from '@c/MasterProductsPageV2';

interface SimilarListingsModuleProps {
  listings: ListingDisplay[];
  brand: string;
}

export default function SimilarListingsModule({ listings, brand }: SimilarListingsModuleProps) {
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  if (listings.length === 0) return null;

  return (
    <section
      aria-label={`Available ${brand} listings on GearFocus`}
      className="max-w-default mx-auto px-[2rem] md:px-[4rem] xl:px-default-padding-x py-[3rem]"
    >
      <h2 className="text-[2.4rem] sm:text-[3.2rem] font-extrabold text-black mb-[2rem]">
        Available Now on GearFocus
      </h2>

      <div className="relative">
        <Swiper
          modules={[Navigation]}
          spaceBetween={16}
          slidesPerView={1}
          navigation={{
            prevEl: '.similar-prev-btn',
            nextEl: '.similar-next-btn',
          }}
          breakpoints={{
            480: { slidesPerView: 2 },
            768: { slidesPerView: 3 },
            1024: { slidesPerView: 4 },
            1280: { slidesPerView: 5 },
          }}
          onInit={(swiper: SwiperType) => {
            setIsBeginning(swiper.isBeginning);
            setIsEnd(swiper.isEnd);
          }}
          onSlideChange={(swiper: SwiperType) => {
            setIsBeginning(swiper.isBeginning);
            setIsEnd(swiper.isEnd);
          }}
        >
          {listings.map((product) => (
            <SwiperSlide key={product.id}>
              <MarketplaceProductCard item={product as ProductDocument} />
            </SwiperSlide>
          ))}
        </Swiper>

        <button
          className={`similar-prev-btn flex h-[4rem] w-[4rem] items-center justify-center rounded-full bg-[#242e5f] text-white cursor-pointer transition-all hover:bg-[#C75008] absolute top-1/2 -left-[1.5rem] -translate-y-1/2 z-10 ${
            isBeginning ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
          aria-label="Previous listings"
        >
          <HiArrowLeft size={20} />
        </button>

        <button
          className={`similar-next-btn flex h-[4rem] w-[4rem] items-center justify-center rounded-full bg-[#242e5f] text-white cursor-pointer transition-all hover:bg-[#C75008] absolute top-1/2 -right-[1.5rem] -translate-y-1/2 z-10 ${
            isEnd ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
          aria-label="Next listings"
        >
          <HiArrowRight size={20} />
        </button>
      </div>
    </section>
  );
}
